import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu } from "lucide-react";

import { SmartLink as Link, SmartNavLink as NavLink } from "./SmartLink";
import Wordmark from "./Wordmark";
import LanguageSwitcher from "./LanguageSwitcher";
import MobileMenu from "./MobileMenu";
import { SITE_NAV } from "@/data/navigation";
import { t } from "@/lib/i18n";
import { fetchJsonDeduped } from "@/lib/dedupedFetch";

// Pulse a small purple dot next to "What's New" in the nav whenever any
// operated service shipped a release in the last 48 hours. The lookup
// hits the same federation cache that the WhatsNewPage uses, so this is
// a cheap signal — no extra polling, no extra storage.
const FRESH_WINDOW_HOURS = 48;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function useRecentReleasePulse() {
  const [hasFresh, setHasFresh] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!API_BASE) return;
    fetchJsonDeduped(`${API_BASE}/api/integrations/ecosystem/changelog?lang=en&pageSize=1`)
      .then((data) => {
        if (cancelled || !data?.items?.length) return;
        const top = data.items[0];
        const iso = top.publishedAtJst || top.sortDate || top.date;
        if (!iso) return;
        const ts = Date.parse(iso);
        if (Number.isNaN(ts)) return;
        const hoursSince = (Date.now() - ts) / (1000 * 60 * 60);
        if (hoursSince <= FRESH_WINDOW_HOURS) setHasFresh(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return hasFresh;
}

/**
 * Single-row, light-only header. No dark utility strip.
 *
 * Layout: wordmark · flat nav · Globe toggle · Contact pill · mobile menu
 */
export default function Header({ lang }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const isJa = lang === "ja";
  const hasFreshRelease = useRecentReleasePulse();

  // Close the mobile menu on route change — but NOT when only the language
  // segment flipped (`/en/x` → `/ja/x`), so visitors can browse JA/EN
  // without losing their place inside the menu.
  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    prevPathRef.current = next;
    if (prev === next) return;
    const a = prev.split("/").filter(Boolean);
    const b = next.split("/").filter(Boolean);
    const isLangOnly =
      a.length > 0 &&
      b.length > 0 &&
      a[0] !== b[0] &&
      a.slice(1).join("/") === b.slice(1).join("/");
    if (!isLangOnly) setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-3 md:gap-6">
          {/*
            Brand link → fast SPA navigation back to homepage.
            Uses React Router's <Link> so it's instant (no full HTML
            re-fetch). `ScrollToTop` already handles top-of-page reset
            on every route change. Hover/active states give clear
            tactile feedback so visitors know the lockup is clickable.
          */}
          <Link
            to={`/${lang}`}
            data-testid="brand-link"
            aria-label={isJa ? "ホームに戻る" : "Back to homepage"}
            className="group shrink-0 min-w-0 inline-flex items-center rounded-lg px-1 -mx-1 py-0.5 transition-all duration-150 cursor-pointer hover:bg-[#F4EEF9] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5B2E83] focus-visible:ring-offset-2"
          >
            <span className="transition-opacity duration-150 group-hover:opacity-85">
              <Wordmark size="header" tone="navy" showSub={false} />
            </span>
          </Link>

          <nav data-testid="primary-nav" className="hidden lg:flex items-center gap-1">
            {SITE_NAV.map((item) =>
              item.children ? (
                <NavDropdown key={item.key} item={item} lang={lang} isJa={isJa} />
              ) : (
                <NavLink
                  key={item.key}
                  to={`/${lang}${item.path}`}
                  data-testid={`nav-${item.key}`}
                  className={({ isActive }) =>
                    [
                      "relative px-3.5 py-2 text-[13.5px] font-display font-semibold tracking-tight transition-colors",
                      isActive ? "text-[#0F1D2D]" : "text-[#475569] hover:text-[#0F1D2D]",
                    ].join(" ")
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="inline-flex items-center gap-1">
                        {t(lang, `nav.${item.key}`)}
                        {item.key === "whatsNew" && hasFreshRelease && (
                          <span
                            aria-label={isJa ? "新しい更新があります" : "Fresh release available"}
                            data-testid="nav-whats-new-pulse"
                            className="relative inline-flex h-1.5 w-1.5"
                          >
                            <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ backgroundColor: "#663399" }} />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#663399" }} />
                          </span>
                        )}
                      </span>
                      {isActive && (
                        <span
                          aria-hidden
                          className="absolute left-2 right-2 -bottom-px h-[3px] rounded-full"
                          style={{ backgroundColor: item.accent, boxShadow: `0 0 12px -2px ${item.accent}66` }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              )
            )}
          </nav>

          {/* Right-side controls — extra breathing room on mobile
              between the green language pill and the hamburger menu
              button so a tap on JA/EN doesn't sit next to the menu
              icon (owner directive 2026-05-21). */}
          <div className="flex items-center gap-4 md:gap-2.5">
            <LanguageSwitcher lang={lang} />
            <Link
              to={`/${lang}/contact`}
              data-testid="header-contact-cta"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#0E5FB0] text-white text-[12px] font-semibold tracking-tight hover:bg-[#084785] transition-colors"
            >
              {isJa ? "お問い合わせ" : "Contact"}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={t(lang, "common.open")}
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full bg-white border border-[#E2E8F0] text-[#0F1D2D] hover:border-[#0E5FB0] hover:text-[#0E5FB0] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0E5FB0] focus-visible:ring-offset-2"
            >
              <Menu className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu lang={lang} open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}

/**
 * NavDropdown — hover/focus-revealed sub-nav for a top-level item that
 * has `children`. The parent label still links to its main path on
 * click, but hovering shows a panel with each child route.
 */
function NavDropdown({ item, lang, isJa }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  // Parent is "active" if the current path matches any of its routes.
  const isActive =
    pathname === `/${lang}${item.path}` ||
    item.children.some((c) => pathname.startsWith(`/${lang}${c.path}`));

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid={`nav-${item.key}-dropdown`}
    >
      <Link
        to={`/${lang}${item.path}`}
        data-testid={`nav-${item.key}`}
        onFocus={handleEnter}
        onBlur={handleLeave}
        aria-expanded={open}
        aria-controls={`nav-${item.key}-dropdown-menu`}
        className={[
          "relative inline-flex items-center gap-1 px-3.5 py-2 text-[13.5px] font-display font-semibold tracking-tight transition-colors",
          isActive ? "text-[#0F1D2D]" : "text-[#475569] hover:text-[#0F1D2D]",
        ].join(" ")}
      >
        {t(lang, `nav.${item.key}`)}
        <ChevronDown
          className={[
            "h-3.5 w-3.5 opacity-70 transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
          strokeWidth={2}
        />
        {isActive && (
          <span
            aria-hidden
            className="absolute left-2 right-2 -bottom-px h-[3px] rounded-full"
            style={{ backgroundColor: item.accent, boxShadow: `0 0 12px -2px ${item.accent}66` }}
          />
        )}
      </Link>

      {open && (
        <div
          data-testid={`nav-${item.key}-dropdown-panel`}
          id={`nav-${item.key}-dropdown-menu`}
          className="absolute left-0 top-full pt-3 z-10"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <div className="min-w-[220px] rounded-2xl bg-white border border-[#E2E8F0] shadow-[0_24px_60px_-20px_rgba(15,29,45,0.25)] overflow-hidden">
            <span
              aria-hidden
              className="block h-1 w-full"
              style={{ backgroundColor: item.accent }}
            />
            <ul className="py-2">
              {item.children.map((c) => {
                const childActive = pathname.startsWith(`/${lang}${c.path}`);
                return (
                  <li key={c.key}>
                    <Link
                      to={`/${lang}${c.path}`}
                      data-testid={`nav-${item.key}-child-${c.key}`}
                      onClick={() => setOpen(false)}
                      className={[
                        "block px-4 py-2.5 text-[13px] font-semibold tracking-tight transition-colors",
                        childActive
                          ? "text-[#0F1D2D] bg-[#F5F0FA]"
                          : "text-[#334155] hover:text-[#0F1D2D] hover:bg-[#F8F9FA]",
                      ].join(" ")}
                    >
                      {isJa ? c.labelJa : c.labelEn}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
